import React, { useEffect, useState, useCallback } from "react";
import {
  Users,
  Wallet,
  TrendingDown,
  ShieldCheck,
  Target,
  ChevronDown,
  Eye,
  PencilLine,
  ExternalLink,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdvisorLayout from "../components/layout/AdvisorLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, fmtINR } from "../lib/api";
import { toast } from "sonner";

// Approach B: the advisor stays inside their own console and the client's data
// is rendered *here* (no full-page shell takeover). A client picker at the top
// switches whose data is shown; tabs mirror the modules the client shared.
const TABS = [
  { key: "overview", label: "Overview", icon: Users, module: null },
  { key: "assets", label: "Assets", icon: Wallet, module: "assets" },
  { key: "liabilities", label: "Liabilities", icon: TrendingDown, module: "liabilities" },
  { key: "insurance", label: "Insurance", icon: ShieldCheck, module: "insurance" },
  { key: "goals", label: "Goals", icon: Target, module: "goals" },
];

export default function AdvisorWorkspace() {
  const { activeClient, selectClient } = useAuth();
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Load the advisor's client list once.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pfa/clients");
        setClients(data.clients || []);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, []);

  const shared = activeClient?.modules || {};

  const pick = (conn) => {
    selectClient({
      id: conn.client_id,
      name: conn.client_name,
      modules: conn.modules || {},
      permission: conn.permission_level || "view",
    });
    setPickerOpen(false);
    setTab("overview");
    setData({});
  };

  // Fetch the client's data for the modules they shared (X-Client-Id header is
  // attached automatically once a client is selected).
  const loadData = useCallback(async () => {
    if (!activeClient) return;
    setLoading(true);
    try {
      const reqs = [];
      if (shared.assets) reqs.push(["assets", api.get("/assets")]);
      if (shared.liabilities) reqs.push(["liabilities", api.get("/liabilities")]);
      if (shared.insurance) reqs.push(["insurance", api.get("/insurance")]);
      if (shared.goals) reqs.push(["goals", api.get("/goals")]);
      const out = {};
      for (const [key, p] of reqs) {
        try {
          const res = await p;
          out[key] = res.data;
        } catch (_) {
          out[key] = null;
        }
      }
      setData(out);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClient?.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const assets = data.assets?.assets || [];
  const liabilities = data.liabilities?.liabilities || [];
  const policies = data.insurance?.policies || [];
  const goals = data.goals?.goals || [];

  const totalAssets = assets.reduce((s, a) => s + Number(a.value || 0), 0);
  // Exclude closed / paid-off liabilities from the outstanding total.
  const totalLiab = liabilities
    .filter((l) => l.is_active !== false)
    .reduce((s, l) => s + Number(l.outstanding || 0), 0);

  const availableTabs = TABS.filter((t) => !t.module || shared[t.module]);

  return (
    <AdvisorLayout>
      <div className="max-w-5xl mx-auto">
        {/* HEADER + CLIENT PICKER */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900 dark:text-white">
              Client Workspace
            </h1>
            <p className="text-sm text-slate-500">
              View a client's data without leaving your console.
            </p>
          </div>

          {/* PICKER */}
          <div className="relative">
            <button
              onClick={() => setPickerOpen((v) => !v)}
              className="flex items-center gap-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2.5 text-sm font-medium min-w-[200px]"
            >
              <span className="grid h-7 w-7 place-items-center rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs font-bold">
                {activeClient?.name?.[0]?.toUpperCase() || "?"}
              </span>
              <span className="flex-1 text-left truncate text-slate-900 dark:text-white">
                {activeClient?.name || "Select a client"}
              </span>
              <ChevronDown className="h-4 w-4 text-slate-400" />
            </button>
            {pickerOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setPickerOpen(false)} />
                <div className="absolute right-0 z-50 mt-1 w-72 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl p-1.5 max-h-80 overflow-y-auto">
                  {clients.length === 0 ? (
                    <div className="px-3 py-4 text-sm text-slate-400 text-center">
                      No connected clients yet.
                    </div>
                  ) : (
                    clients.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => pick(c)}
                        className={`w-full flex items-center gap-2.5 rounded-xl px-3 py-2 text-left hover:bg-slate-100 dark:hover:bg-slate-800 ${
                          activeClient?.id === c.client_id
                            ? "bg-emerald-50 dark:bg-emerald-500/10"
                            : ""
                        }`}
                      >
                        <span className="grid h-8 w-8 place-items-center rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 text-xs font-bold">
                          {c.client_name?.[0]?.toUpperCase() || "C"}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-slate-900 dark:text-white">
                            {c.client_name}
                          </span>
                          <span className="block text-[11px] text-slate-400">
                            {c.permission_level === "suggest"
                              ? "View + Edit"
                              : "View only"}
                          </span>
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {!activeClient ? (
          <EmptyPick />
        ) : (
          <>
            {/* CONTEXT STRIP */}
            <div className="flex items-center justify-between gap-3 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/60 dark:border-emerald-500/20 px-4 py-2.5 mb-4">
              <div className="flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-200">
                {activeClient.permission === "suggest" ? (
                  <PencilLine className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                <span>
                  Viewing <b>{activeClient.name}</b> ·{" "}
                  {activeClient.permission === "suggest"
                    ? "you can propose changes from the full view"
                    : "read only"}
                </span>
              </div>
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300 hover:underline whitespace-nowrap"
              >
                Open full view <ExternalLink className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* TABS */}
            <div className="flex gap-1 flex-wrap border-b border-slate-200 dark:border-slate-800 mb-5">
              {availableTabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  className={`flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium border-b-2 -mb-px transition ${
                    tab === t.key
                      ? "border-emerald-500 text-emerald-700 dark:text-emerald-300"
                      : "border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  }`}
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="py-16 text-center text-slate-400">Loading client data…</div>
            ) : tab === "overview" ? (
              <Overview
                clientName={activeClient?.name}
                totalAssets={totalAssets}
                totalLiab={totalLiab}
                topGoal={goals[0]}
                counts={{
                  assets: assets.length,
                  liabilities: liabilities.length,
                  insurance: policies.length,
                  goals: goals.length,
                }}
                shared={shared}
                onTab={setTab}
              />
            ) : tab === "assets" ? (
              <DataTable
                cols={["Name", "Category", "Value", "Return"]}
                rows={assets.map((a) => [
                  a.name,
                  a.category,
                  fmtINR(a.value),
                  a.expected_return != null ? `${a.expected_return}%` : "—",
                ])}
                empty="No assets."
              />
            ) : tab === "liabilities" ? (
              <DataTable
                cols={["Name", "Category", "Outstanding", "EMI"]}
                rows={liabilities.map((l) => [
                  l.name,
                  l.category,
                  fmtINR(l.outstanding),
                  l.emi ? fmtINR(l.emi) : "—",
                ])}
                empty="No liabilities."
              />
            ) : tab === "insurance" ? (
              <DataTable
                cols={["Policy", "Type", "Insurer", "Cover", "Premium/yr"]}
                rows={policies.map((p) => [
                  p.name,
                  p.policy_type,
                  p.insurer || "—",
                  p.sum_assured ? fmtINR(p.sum_assured) : "—",
                  p.annual_premium ? fmtINR(p.annual_premium) : "—",
                ])}
                empty="No policies."
              />
            ) : tab === "goals" ? (
              <GoalsList goals={goals} />
            ) : null}
          </>
        )}
      </div>
    </AdvisorLayout>
  );
}

function EmptyPick() {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-12 text-center">
      <div className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-500">
        <Users className="h-6 w-6" />
      </div>
      <p className="text-sm text-slate-500">
        Select a client above to see their financial data here.
      </p>
    </div>
  );
}

function Overview({ clientName, totalAssets, totalLiab, topGoal, counts, shared, onTab }) {
  const netWorth = totalAssets - totalLiab;
  const total = Math.max(totalAssets + totalLiab, 1);
  const assetPct = Math.round((totalAssets / total) * 100);
  const showFinance = shared.dashboard || shared.assets || shared.liabilities;

  return (
    <div className="space-y-5">
      {/* HERO NET WORTH — light, modern card */}
      {showFinance && (
        <div className="relative overflow-hidden rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
          {/* subtle emerald glow, no heavy fill */}
          <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-emerald-400/10 blur-3xl" />
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">
              {clientName ? `${clientName}'s net worth` : "Net worth"}
            </div>
            <div className="mt-1 font-display text-4xl font-bold text-slate-900 dark:text-white">
              {fmtINR(netWorth)}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 max-w-sm">
              <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 px-4 py-3">
                <div className="text-[11px] text-emerald-700/70 dark:text-emerald-300/70">
                  Assets
                </div>
                <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300">
                  {fmtINR(totalAssets)}
                </div>
              </div>
              <div className="rounded-2xl bg-rose-50 dark:bg-rose-500/10 px-4 py-3">
                <div className="text-[11px] text-rose-700/70 dark:text-rose-300/70">
                  Liabilities
                </div>
                <div className="text-lg font-bold text-rose-600 dark:text-rose-300">
                  {fmtINR(totalLiab)}
                </div>
              </div>
            </div>

            {/* ratio bar */}
            <div className="mt-4 flex h-2 w-full max-w-sm overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div className="h-full bg-emerald-500" style={{ width: `${assetPct}%` }} />
              <div className="h-full bg-rose-400" style={{ width: `${100 - assetPct}%` }} />
            </div>
            <div className="mt-1 text-[11px] text-slate-400">
              {assetPct}% assets · {100 - assetPct}% liabilities
            </div>
          </div>
        </div>
      )}

      {/* MODULE TILES */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile icon={Wallet} label="Assets" n={counts.assets} on={shared.assets} tone="emerald" onClick={() => shared.assets && onTab("assets")} />
        <Tile icon={TrendingDown} label="Liabilities" n={counts.liabilities} on={shared.liabilities} tone="rose" onClick={() => shared.liabilities && onTab("liabilities")} />
        <Tile icon={ShieldCheck} label="Insurance" n={counts.insurance} on={shared.insurance} tone="teal" onClick={() => shared.insurance && onTab("insurance")} />
        <Tile icon={Target} label="Goals" n={counts.goals} on={shared.goals} tone="amber" onClick={() => shared.goals && onTab("goals")} />
      </div>

      {/* TOP GOAL */}
      {shared.goals && topGoal && (
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
            A goal in progress
          </div>
          <div className="flex items-center justify-between">
            <div className="font-semibold text-slate-900 dark:text-white">
              {topGoal.name}
            </div>
            <div className="text-sm text-slate-500">
              {fmtINR(topGoal.current_amount)} / {fmtINR(topGoal.target_amount)}
            </div>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div
              className="h-full bg-emerald-500"
              style={{
                width: `${Math.min(100, Math.round((Number(topGoal.current_amount || 0) / Number(topGoal.target_amount || 1)) * 100))}%`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ icon: Icon, label, n, on, tone, onClick }) {
  const tones = {
    emerald: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600",
    rose: "bg-rose-50 dark:bg-rose-500/10 text-rose-600",
    teal: "bg-teal-50 dark:bg-teal-500/10 text-teal-600",
    amber: "bg-amber-50 dark:bg-amber-500/10 text-amber-600",
  };
  return (
    <button
      onClick={onClick}
      disabled={!on}
      className={`rounded-2xl border p-4 text-left transition ${
        on
          ? "border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:shadow-md hover:border-emerald-300"
          : "border-dashed border-slate-200 dark:border-slate-800 opacity-50 cursor-default"
      }`}
    >
      <span className={`grid h-9 w-9 place-items-center rounded-xl ${tones[tone]}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="mt-2 text-xl font-bold text-slate-900 dark:text-white">
        {on ? n : "—"}
      </div>
      <div className="text-[11px] text-slate-500">{label}</div>
    </button>
  );
}

function DataTable({ cols, rows, empty }) {
  if (!rows.length)
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center text-sm text-slate-400">
        {empty}
      </div>
    );
  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 dark:bg-slate-800/60 text-left">
            {cols.map((c) => (
              <th
                key={c}
                className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500"
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              className="border-t border-slate-100 dark:border-slate-800"
            >
              {r.map((cell, j) => (
                <td
                  key={j}
                  className={`px-4 py-2.5 ${
                    j === 0
                      ? "font-medium text-slate-900 dark:text-white"
                      : "text-slate-600 dark:text-slate-300"
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GoalsList({ goals }) {
  if (!goals.length)
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-10 text-center text-sm text-slate-400">
        No goals.
      </div>
    );
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {goals.map((g) => {
        const pct = Math.min(
          100,
          Math.round((Number(g.current_amount || 0) / Number(g.target_amount || 1)) * 100),
        );
        return (
          <div
            key={g.id}
            className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5"
          >
            <div className="flex items-center justify-between">
              <div className="font-semibold text-slate-900 dark:text-white">
                {g.name}
              </div>
              <div className="text-xs text-slate-400">
                {g.target_date ? new Date(g.target_date).getFullYear() : ""}
              </div>
            </div>
            <div className="mt-2 text-sm text-slate-500">
              {fmtINR(g.current_amount)} of {fmtINR(g.target_amount)}
            </div>
            <div className="mt-2 h-2 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
              <div
                className="h-full bg-emerald-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
